import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import i18n, {
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  detectUrlPrefixLocale,
} from "../i18n";
import {
  getStoredAmbassadorRef,
  clearStoredAmbassadorRef,
} from "../lib/ambassadorRef";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Sync the user's persisted server-side locale onto the in-browser i18n
// stack the moment auth resolves. The detector falls back to the browser
// language for new users — but once a user has explicitly set their
// preference, the server is the source of truth so it stays consistent
// across devices. Null/missing/unsupported locales no-op gracefully.
//
// One exception: when the user lands on a locale-prefixed URL (`/fr/…`
// or `/es/…`), that URL is the strongest signal of intent — a French
// crawler / shared link / language-tester explicitly asking for FR.
// We honour that ahead of the stored `user.locale` so a US-resident
// admin browsing the French marketing snapshot still sees French copy
// on the routine viewer. (Previously caused the mood-picker chip
// labels to render English on /fr/routine/:id — iteration_26 + 27 RCA.)
const syncUserLocale = (locale) => {
  const urlLocale = detectUrlPrefixLocale();
  const target = urlLocale || locale;
  if (!target || !SUPPORTED_LOCALES.includes(target)) return;
  if (i18n.language === target) return;
  try {
    i18n.changeLanguage(target);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, target);
    }
  } catch {
    // i18n failures shouldn't break login.
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      syncUserLocale(data?.locale);
      // Ambassador-attribution: if a ?ref= handle was captured before
      // this user signed up, bind it now (idempotent on the server,
      // first-touch wins). Then clear the local store so the same
      // ref can't be re-attributed if they sign up again on this
      // device.
      const ref = getStoredAmbassadorRef();
      if (ref && data?.user_id) {
        try {
          await axios.post(
            `${API}/ambassador/attribute`,
            { handle: ref },
            { withCredentials: true },
          );
        } catch {
          /* attribution is best-effort */
        }
        clearStoredAmbassadorRef();
      }
      // Demo-session claim: if the user came from the homepage soft-
      // continuation CTA (or just had a guest conversation going), bind
      // the demo_trouve session to their account so it shows up in
      // their chat history. Idempotent on the server, silent on failure.
      try {
        const raw = localStorage.getItem("trubeaute.demo.trouve.v1");
        if (raw && data?.user_id) {
          const stored = JSON.parse(raw);
          if (stored?.session_id) {
            await axios.post(
              `${API}/demo/trouve/sessions/${encodeURIComponent(stored.session_id)}/claim`,
              {},
              { withCredentials: true },
            ).catch(() => { /* expired or already claimed — silent */ });
          }
        }
      } catch {
        /* claim is best-effort */
      }
    } catch (err) {
      // 401 is the expected unauthenticated path — only log unusual errors.
      if (
        process.env.NODE_ENV !== "production" &&
        err?.response?.status &&
        err.response.status !== 401
      ) {
        // eslint-disable-next-line no-console
        console.debug("[auth] /auth/me failed:", err.response.status);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from the Google OAuth callback, skip the /me check —
    // AuthCallback will exchange the authorization code and establish
    // the session first.
    if (window.location.pathname === "/auth/google") {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      // Network blip during logout is harmless — local state is wiped below.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[auth] logout request failed:", err?.message || err);
      }
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
